<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import useReaderStore from '../../stores/reader';
import useLocale from '../../composables/useLocale';
import { NAVIGATION_ITEMS, type NavigationItem } from '../../types';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';

library.add([faChevronLeft]);

type Emits = {
  (e: 'closeAnnotations'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  annotationItems?: NavigationItem[];
};
const props = defineProps<Props>();

const { t } = useLocale();

const { hasBookmarks, hasHighlights, isAnnotationBtnsControls } = storeToRefs(useReaderStore());

const search = ref('');
const tab = ref(0);

const filteredAnnotationItems = computed<NavigationItem[]>(() => {
  const items = props.annotationItems ?? [];

  return items.filter((item) => {
    if (item === NAVIGATION_ITEMS.BOOKMARKS_LIST) return hasBookmarks.value;
    if (item === NAVIGATION_ITEMS.HIGHLIGHT_LIST) return hasHighlights.value;
    return false;
  });
});

const getTabTitle = (item: NavigationItem): string => {
  const suffixMap: Record<string, string> = {
    [NAVIGATION_ITEMS.BOOKMARKS_LIST]: 'bookmarks',
    [NAVIGATION_ITEMS.HIGHLIGHT_LIST]: 'highlights',
  };
  return t(`book_player.label_${suffixMap[item] ?? 'bookmarks'}`);
};

const resetSearch = () => (search.value = '');
</script>

<template>
  <div>
    <template v-if="annotationItems">
      <div class="nav-top-bar my-2">
        <v-btn
          v-if="isAnnotationBtnsControls"
          size="48"
          variant="text"
          :title="t('label_close')"
          @click="emit('closeAnnotations')"
        >
          <FontAwesomeIcon icon="fas fa-chevron-right" />
        </v-btn>
        <v-toolbar-title>{{ t('book_player.label_annotations') }}</v-toolbar-title>
      </div>
      <v-tabs
        v-model="tab"
        bg-color="primary-container"
        color="on-primary-container"
        grow
        hide-slider
        height="40"
        class="rounded text-on-primary-container"
        selected-class="bg-primary text-on-primary"
      >
        <v-tab v-for="item in filteredAnnotationItems" :key="item" :text="getTabTitle(item)" rounded="sm" />
      </v-tabs>

      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        :label="t('book_player.label_search')"
        :aria-label="t('book_player.label_search')"
        density="compact"
        variant="outlined"
        class="my-2"
        hide-details
      />
    </template>
    <slot :tab="tab" :search="search" :reset-search="resetSearch" :annotation-items="filteredAnnotationItems" />
  </div>
</template>

<style scoped lang="scss">
.nav-top-bar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;

  .v-toolbar-title {
    font-size: 20px;
    font-family: 'Filson-Soft', sans-serif;
  }
}
</style>
